import React, { FC, Fragment } from 'react';
import {
  Box,
  Text,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModalState,
  Button,
  ButtonAppearance,
} from '@bechtel/gds';
import { PlanningError, PlanningErrorCode } from '@bechtel/types';
import { CommodityMilestoneStyled, PlanErrorHeadingStyled } from './styled';
import { Trans } from '@lingui/macro';

interface PlanMilestoneErrorProps {
  planningError: PlanningError[];
  setPlanningError: (planningError: PlanningError[]) => void;
}

const getErrorDescFromCode = (errorCode: PlanningErrorCode) => {
  switch (errorCode) {
    case PlanningErrorCode.INVALID_PLANNING_WEEK:
      return <Trans>Milestone(s) moved to invalid planning week</Trans>;
    case PlanningErrorCode.NO_MILESTONES_PROVIDED:
      return <Trans>No milestone(s) provided</Trans>;
    case PlanningErrorCode.MILESTONE_IS_CLAIMED:
      return <Trans>Milestone(s) already claimed and deleted from the plan</Trans>;
    case PlanningErrorCode.MILESTONE_IS_APPROVED:
      return <Trans>Milestone(s) already approved and deleted from the plan</Trans>;
    case PlanningErrorCode.MILESTONES_NOT_PLANNED:
      return <Trans>Milestone(s) not planned</Trans>;
    case PlanningErrorCode.MILESTONES_ALREADY_PLANNED:
      return <Trans>Milestone(s) already Planned</Trans>;
    case PlanningErrorCode.PLANNING_WEEK_OUT_OF_WORKPACKAGE_SCHEDULE:
      return <Trans>Milestone(s) schedule outside planning week schedule</Trans>;
    default:
      return <Trans>Milestone(s) errored</Trans>;
  }
};

export const PlanMilestoneError: FC<PlanMilestoneErrorProps> = ({ planningError, setPlanningError }) => {
  const { visible, close } = useModalState(true);
  return (
    <Modal visible={visible}>
      <ModalHeader title="Milestone(s) Not Planned"></ModalHeader>
      <ModalBody>
        <Box
          css={`
            flex-direction: column;
            flex-grow: 1;
          `}
        >
          {planningError &&
            planningError.map((error) => {
              return (
                <Fragment key={error.errorCode}>
                  <PlanErrorHeadingStyled data-testid="plan-error-code">
                    {getErrorDescFromCode(error.errorCode)}
                  </PlanErrorHeadingStyled>
                  {error &&
                    error.commodityMilestones &&
                    error.commodityMilestones.map((milestone) => {
                      const commodity = milestone.commodity.weld || milestone.commodity.materialCommodity;
                      return (
                        <CommodityMilestoneStyled key={milestone.name} data-testid="plan-error-milestone">
                          <Box pb={{ sm: '400' }} pt={{ sm: '100' }}>
                            <Text size="500" family="graphik" color="grey.1000" weight="600">
                              {commodity?.type.name}:&nbsp;
                            </Text>
                            <Text size="500" family="graphik" color="grey.1000">
                              {commodity?.tag}
                            </Text>
                          </Box>
                          <Box pb={{ sm: '100' }}>
                            <Text size="500" family="graphik" color="grey.1000" weight="600">
                              <Trans>Milestone:</Trans>&nbsp;
                            </Text>
                            <Text size="500" family="graphik" color="grey.1000">
                              {milestone.name}
                            </Text>
                          </Box>
                        </CommodityMilestoneStyled>
                      );
                    })}
                </Fragment>
              );
            })}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box>
            <Button
              type="submit"
              appearance={ButtonAppearance.SECONDARY}
              onClick={() => {
                setPlanningError([]);
                close();
              }}
            >
              <Trans>Close</Trans>
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
